import React, {
  ChangeEvent,
  CSSProperties,
  ReactElement,
  useState,
} from 'react'
import styled from 'styled-components'
import FieldStyles, { FieldSize } from '../components/FieldStyles'
import { hasForbiddenChars, maxCharsDisplay } from '../helpers/fieldHelper'

const StyledTextArea = styled.textarea`
  &.text-area-disabled {
    resize: none;
  }
  &.text-area-large {
    font-size: 16px;
    padding: 8px;
  }
`

export type TextAreaProps = {
  name?: string
  placeholder?: string
  value?: string
  onChange?: (value: string) => void
  className?: string
  style?: CSSProperties
  rows?: number
  label?: string
  isError?: boolean
  disabled?: boolean
  maxChars?: number
  hideMaxChars?: boolean
  subtitle?: string
  fieldSize?: FieldSize
  noLineBreaks?: boolean
}

function TextArea(props: TextAreaProps): ReactElement {
  const {
    name,
    placeholder,
    isError,
    rows,
    onChange,
    value,
    disabled,
    className,
    style,
    label,
    maxChars,
    subtitle,
    fieldSize,
    hideMaxChars,
    noLineBreaks,
  } = props
  const [contentError, setContentError] = useState('')

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>): void => {
    const existingValue = value && typeof value === 'string' ? value : ''
    const newValue = event.target.value

    if (hasForbiddenChars(newValue)) {
      setContentError('Character not allowed')
      return
    }
    // if we are over max characters AND we are not trying to reduce character count
    if (
      maxChars
      && newValue.length > maxChars
      && newValue.length > existingValue.length
    ) {
      setContentError('Over character limit')
      return
    }
    setContentError('')

    if (noLineBreaks && /\r|\n/.test(newValue)) {
      setContentError('Line breaks not allowed')
      return
    }
    setContentError('')

    if (onChange) onChange(newValue)
  }

  const errorClass = isError || contentError ? 'error' : ''
  const disabledClass = disabled ? 'text-area-disabled' : 'text-area-enabled'
  const sizeClass = fieldSize ? `text-area-${fieldSize}` : ''

  return (
    <FieldStyles
      label={label}
      className={className}
      style={style}
      maxChars={maxCharsDisplay(value?.toString().length, maxChars)}
      hideMaxChars={hideMaxChars}
      subtitle={subtitle}
      error={contentError}
    >
      <StyledTextArea
        name={name}
        placeholder={placeholder}
        className={`styled-input ${errorClass} ${disabledClass} ${sizeClass}`}
        onChange={handleChange}
        value={value}
        rows={rows}
        disabled={disabled}
      />
    </FieldStyles>
  )
}
export default TextArea
